import { useEffect, useLayoutEffect, useRef, type KeyboardEvent, type PointerEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { DEFAULT_PAPER_SPAN, clampPaperSpan, type PaperSpan } from '../hooks/usePaperSpan';

interface PaperResizeHandleProps {
  readonly span: PaperSpan;
  readonly onChange: (span: PaperSpan, commit: boolean) => void;
  /** The mosaic's grid, measured here (it changes with the window): its column count, and the
   *  card size and row gap the paper's exact height is built from (#753). */
  readonly onMosaicMeasured: (mosaic: MosaicMeasure) => void;
  readonly maxColumns: number;
}

interface MosaicUnit {
  readonly column: number;
  readonly row: number;
  readonly columns: number;
  readonly track: number;
  readonly rowGap: number;
}

export interface MosaicMeasure {
  readonly columns: number;
  /** One result card's width (a grid track); its height is 9/16 of it. */
  readonly track: number;
  readonly rowGap: number;
}

// The mosaic's grid unit in px: one result card plus the gap, across (a column) and down (a 16:9
// row, the card's aspect ratio), and how many columns the grid currently has.
function measureMosaic(mosaic: Element): MosaicUnit | null {
  const style = getComputedStyle(mosaic);
  const tracks = style.gridTemplateColumns.split(' ').map(Number.parseFloat).filter(Number.isFinite);
  const [track] = tracks;
  if (!track) return null;
  const columnGap = Number.parseFloat(style.columnGap) || 0;
  const rowGap = Number.parseFloat(style.rowGap) || 0;
  return { column: track + columnGap, row: (track * 9) / 16 + rowGap, columns: tracks.length, track, rowGap };
}

/**
 * #737 -- the paper's bottom-right resize corner. Dragging snaps the paper to whole result cards
 * (live, so the cards beside and below reflow as it moves); arrow keys do the same one card at a
 * time; Home or a double-click goes back to 2x2. Pointer events stay on the handle, so the
 * drawing underneath never sees the drag.
 */
export function PaperResizeHandle({ span, onChange, onMosaicMeasured, maxColumns }: PaperResizeHandleProps) {
  const { t } = useTranslation();
  const ref = useRef<HTMLButtonElement | null>(null);
  const drag = useRef<{ pointerId: number; x: number; y: number; start: PaperSpan; unit: MosaicUnit } | null>(null);

  // The grid the paper is in -- checked after every render, not only on mount: the paper can be
  // drawn before it sits in the results grid and move into it later without remounting (#753:
  // the measurement was then never taken, so the paper kept its approximate height).
  const observed = useRef<{ mosaic: Element; observer: ResizeObserver } | null>(null);
  const lastReport = useRef('');
  useLayoutEffect(() => {
    const mosaic = ref.current?.closest('.workspace-mosaic') ?? null;
    if (observed.current?.mosaic === mosaic) return;
    observed.current?.observer.disconnect();
    observed.current = null;
    if (!mosaic) return;
    const report = () => {
      const unit = measureMosaic(mosaic);
      if (!unit) return;
      const next = { columns: unit.columns, track: unit.track, rowGap: unit.rowGap };
      const key = JSON.stringify(next);
      if (key === lastReport.current) return;
      lastReport.current = key;
      onMosaicMeasured(next);
    };
    const observer = new ResizeObserver(report);
    observer.observe(mosaic);
    observed.current = { mosaic, observer };
    report();
  });
  useEffect(
    () => () => {
      observed.current?.observer.disconnect();
      observed.current = null;
    },
    []
  );

  const onPointerDown = (event: PointerEvent<HTMLButtonElement>) => {
    if (event.button !== 0) return;
    const mosaic = event.currentTarget.closest('.workspace-mosaic');
    const unit = mosaic ? measureMosaic(mosaic) : null;
    if (!unit) return;
    event.preventDefault();
    event.stopPropagation();
    event.currentTarget.setPointerCapture(event.pointerId);
    drag.current = { pointerId: event.pointerId, x: event.clientX, y: event.clientY, start: span, unit };
  };

  const spanAt = (event: PointerEvent<HTMLButtonElement>): PaperSpan | null => {
    const current = drag.current;
    if (!current || current.pointerId !== event.pointerId) return null;
    return clampPaperSpan(
      {
        columns: current.start.columns + Math.round((event.clientX - current.x) / current.unit.column),
        rows: current.start.rows + Math.round((event.clientY - current.y) / current.unit.row)
      },
      current.unit.columns
    );
  };

  const onPointerMove = (event: PointerEvent<HTMLButtonElement>) => {
    const next = spanAt(event);
    if (next) onChange(next, false);
  };

  const endDrag = (event: PointerEvent<HTMLButtonElement>) => {
    const next = spanAt(event);
    if (!next) return;
    drag.current = null;
    onChange(next, true);
  };

  const onPointerCancel = (event: PointerEvent<HTMLButtonElement>) => {
    const current = drag.current;
    if (!current || current.pointerId !== event.pointerId) return;
    drag.current = null;
    onChange(current.start, false);
  };

  const onKeyDown = (event: KeyboardEvent<HTMLButtonElement>) => {
    const step: Record<string, Partial<Record<'columns' | 'rows', number>>> = {
      ArrowRight: { columns: 1 },
      ArrowLeft: { columns: -1 },
      ArrowDown: { rows: 1 },
      ArrowUp: { rows: -1 }
    };
    if (event.key === 'Home') {
      event.preventDefault();
      onChange(clampPaperSpan(DEFAULT_PAPER_SPAN, maxColumns), true);
      return;
    }
    const delta = step[event.key];
    if (!delta) return;
    event.preventDefault();
    const current = span;
    onChange(
      clampPaperSpan(
        { columns: current.columns + (delta.columns ?? 0), rows: current.rows + (delta.rows ?? 0) },
        maxColumns
      ),
      true
    );
  };

  const label = t('paperResize.label');
  const size = t('paperResize.size', { columns: span.columns, rows: span.rows });
  return (
    <button
      ref={ref}
      type="button"
      className="paper-resize-handle"
      data-testid="paper-resize-handle"
      aria-label={`${label}, ${size}`}
      title={`${label} (${size}). ${t('paperResize.hint')}`}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={endDrag}
      onPointerCancel={onPointerCancel}
      onLostPointerCapture={onPointerCancel}
      onDoubleClick={() => onChange(clampPaperSpan(DEFAULT_PAPER_SPAN, maxColumns), true)}
      onKeyDown={onKeyDown}
    >
      <svg viewBox="0 0 16 16" width="14" height="14" aria-hidden="true" focusable="false">
        <path
          d="M15 5 5 15M15 10l-5 5M15 15h0"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.6"
          strokeLinecap="round"
        />
      </svg>
    </button>
  );
}
